import {
  BlockTitle,
  Button,
  Field,
  QAPair,
  Select,
  StatusTag,
  Surface,
  Textarea,
  formatDate,
  titleCase,
} from '@westhetics/ui'
import { useState } from 'react'
import { useSession } from '@/lib/session'
import type { Anamnesis, AnamnesisChannel } from './api'
import {
  CHANNELS,
  CHANNEL_LABELS,
  STATUS_LEVELS,
  canCancel,
  canResend,
  draftLine,
  draftSince,
  formatDateTime,
  isLinkExpired,
  latestDeliveries,
} from './labels'
import { LinkPanel } from './link-panel'
import { useAnamnesisActions } from './use-anamnesis-actions'

/**
 * O registro de uma anamnese que não foi preenchida (RN-ANA-21): abre no mesmo lugar da leitura e
 * mostra o envio, a entrega, o link com o QR, onde a paciente parou e o que dá para fazer —
 * reenviar (RN-ANA-04) e cancelar (RN-ANA-05). Nunca as respostas do rascunho. A cancelada mostra
 * quem cancelou, quando e por quê.
 */

const lineClass = 'font-body text-secondary text-[12px] leading-[1.6] font-light'

export function AnamnesisRecord({
  anamnesis,
  onBack,
}: {
  anamnesis: Anamnesis
  onBack: () => void
}) {
  const session = useSession()
  const actions = useAnamnesisActions()
  const name = titleCase(anamnesis.patient.name)
  const expired = isLinkExpired(anamnesis)
  const isCanceled = anamnesis.status === 'canceled'
  const mayResend = canResend(anamnesis)
  const mayCancel = canCancel(session, anamnesis)

  const [channel, setChannel] = useState<AnamnesisChannel>(anamnesis.channel ?? 'whatsapp')
  const [cancelOpen, setCancelOpen] = useState(false)
  const [reason, setReason] = useState('')

  const deliveries = latestDeliveries(anamnesis.delivery)
  const expiresOn = formatDate(new Date(anamnesis.link.expiresAt))
  const resendCount = anamnesis.link.resendCount
  const since = draftSince(anamnesis.draft)

  const identification = [
    name,
    anamnesis.procedureAtSendTime
      ? `procedimento: ${anamnesis.procedureAtSendTime.name}`
      : 'sem procedimento',
    anamnesis.practitioner
      ? `profissional: ${titleCase(anamnesis.practitioner.name)}`
      : 'sem profissional',
  ].join(' · ')

  return (
    <article aria-label="Anamnese não preenchida" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <Button variant="text" onClick={onBack}>
          ← Voltar à lista
        </Button>
        <nav className={lineClass} aria-label="Anamnese aberta">
          {name} / anamneses /{' '}
          <span className="text-ink">{formatDate(new Date(anamnesis.createdAt))}</span>
        </nav>
      </div>

      <BlockTitle
        title={isCanceled ? 'Anamnese cancelada' : 'Anamnese enviada, ainda não preenchida'}
        note={identification}
        trailing={<StatusTag level={STATUS_LEVELS[anamnesis.status]}>{anamnesis.label}</StatusTag>}
      />

      {actions.notice}

      <Surface padding="none" className="px-4" role="group" aria-label="Situação do envio">
        <div className="tablet:grid-cols-2 grid grid-cols-1 gap-x-6">
          <QAPair
            className="border-b-0"
            question="Envio"
            answer={
              anamnesis.createdBy
                ? `${formatDateTime(anamnesis.createdAt)} por ${titleCase(anamnesis.createdBy.name)}`
                : formatDateTime(anamnesis.createdAt)
            }
          />
          <QAPair
            className="max-tablet:border-t border-b-0"
            question="Entrega"
            answer={
              deliveries.length > 0 ? (
                <>
                  {deliveries.map((event) => (
                    <span key={event.channel} className="block">
                      {event.label} <span className={lineClass}>em {formatDateTime(event.at)}</span>
                    </span>
                  ))}
                </>
              ) : anamnesis.channel === 'link' ? (
                'Só o link: o sistema não enviou mensagem'
              ) : (
                'Sem retorno do provedor ainda'
              )
            }
          />
          {isCanceled ? (
            <QAPair
              className="tablet:col-span-2 border-t border-b-0"
              question="Cancelamento"
              answer={
                anamnesis.cancellation ? (
                  <>
                    Cancelada em {formatDateTime(anamnesis.cancellation.at)}
                    {anamnesis.cancellation.by
                      ? ` por ${titleCase(anamnesis.cancellation.by.name)}`
                      : ''}
                    {anamnesis.cancellation.reason ? (
                      <span className={`${lineClass} mt-1 block`}>
                        Motivo: {anamnesis.cancellation.reason}
                      </span>
                    ) : null}
                  </>
                ) : (
                  'Cancelada pela clínica'
                )
              }
            />
          ) : (
            <>
              <QAPair
                className="border-t border-b-0"
                question="Link"
                risk={expired}
                answer={
                  <>
                    {expired ? `Venceu em ${expiresOn}` : `Vale até ${expiresOn}`}
                    {resendCount > 0 ? (
                      <span className={`${lineClass} mt-1 block`}>
                        {resendCount === 1 ? 'reenviado 1 vez' : `reenviado ${resendCount} vezes`}
                      </span>
                    ) : null}
                  </>
                }
              />
              <QAPair
                className="border-t border-b-0"
                question="Preenchimento"
                answer={
                  anamnesis.draft ? (
                    <>
                      {draftLine(anamnesis.draft)}
                      {since ? <span className={`${lineClass} mt-1 block`}>{since}</span> : null}
                    </>
                  ) : anamnesis.draftPurgedAt ? (
                    <>
                      Rascunho apagado em {formatDate(new Date(anamnesis.draftPurgedAt))}
                      <span className={`${lineClass} mt-1 block`}>
                        O link ficou vencido por 90 dias. Reenviar recomeça do zero.
                      </span>
                    </>
                  ) : (
                    'A paciente ainda não começou'
                  )
                }
              />
            </>
          )}
        </div>
      </Surface>

      {isCanceled ? (
        <p className={lineClass}>
          O link desta anamnese não funciona mais e o que havia sido respondido foi apagado. Se a
          paciente ainda precisar responder, envie uma nova anamnese.
        </p>
      ) : null}

      {!isCanceled && !expired && anamnesis.link.url ? (
        <Surface className="max-desk:px-4">
          <LinkPanel
            url={anamnesis.link.url}
            patientName={anamnesis.patient.name}
            expiresAt={anamnesis.link.expiresAt}
          />
        </Surface>
      ) : null}

      {mayResend ? (
        <Surface className="max-desk:px-4">
          <h2 className="font-display text-lead text-ink leading-[1.3] font-normal">
            Reenviar o link
          </h2>
          <p className={`${lineClass} mt-1 max-w-[560px]`}>
            {expired
              ? 'O link venceu. Reenviar gera um link novo para a mesma anamnese, válido por 7 dias; o que ela já respondeu continua guardado.'
              : 'Gera um link novo para a mesma anamnese, válido por 7 dias. O anterior deixa de valer e o que ela já respondeu continua guardado.'}
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <Field label="Reenviar por" className="tablet:w-[240px] w-full">
              <Select
                value={channel}
                onChange={(event) => setChannel(event.target.value as AnamnesisChannel)}
              >
                {CHANNELS.map((option) => (
                  <option key={option} value={option}>
                    {CHANNEL_LABELS[option]}
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              variant="outline"
              loading={actions.isResending}
              loadingLabel="Reenviando…"
              onClick={() => actions.resend(anamnesis, channel)}
            >
              {channel === 'link' ? 'Gerar link novo' : 'Reenviar'}
            </Button>
          </div>
        </Surface>
      ) : null}

      {mayCancel ? (
        <Surface className="max-desk:px-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="max-w-[520px]">
              <h2 className="font-display text-lead text-ink leading-[1.3] font-normal">
                Cancelar a anamnese
              </h2>
              <p className={`${lineClass} mt-1`}>
                O link deixa de funcionar e o rascunho é apagado. O registro do envio continua no
                histórico de {name}; nada do cadastro se perde.
              </p>
            </div>
            {cancelOpen ? null : (
              <Button variant="outline" onClick={() => setCancelOpen(true)}>
                Cancelar anamnese
              </Button>
            )}
          </div>
          {cancelOpen ? (
            <div className="border-hairline mt-4 flex flex-col gap-4 border-t pt-4">
              <Field
                label="Motivo"
                optional
                meta={`${reason.trim().length}/200`}
                hint="Fica no histórico da paciente, com o seu nome e a data."
              >
                <Textarea
                  autoFocus
                  value={reason}
                  maxLength={200}
                  onChange={(event) => setReason(event.target.value)}
                />
              </Field>
              <div className="flex flex-wrap items-center gap-4">
                <Button variant="outline" onClick={() => actions.askCancel(anamnesis, reason)}>
                  Cancelar anamnese
                </Button>
                <Button variant="text" onClick={() => setCancelOpen(false)}>
                  Desistir de cancelar
                </Button>
              </div>
            </div>
          ) : null}
        </Surface>
      ) : null}

      {actions.dialog}
    </article>
  )
}
